import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { MessageSquare } from 'lucide-react';
import z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { User } from 'lucide-react';
import Image from 'next/image';
import { MessageSquareText } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { showToast } from '@/lib/toast';
import { handleApiErrors } from '@/lib/api-error-utils';
import { Loader2 } from 'lucide-react';
import { Sparkles } from 'lucide-react';
import { useSession } from 'next-auth/react';
import { ArrowRight } from 'lucide-react';
import { getProfile } from '@/app/actions/users.actions';
import { Checkbox } from './ui/checkbox';
import Link from 'next/link';
import { cn } from '@/lib/utils';

const FormSchema = z.object({
  // name: z.string("Name must be a string"),
  // email: z.string().email("Invalid email address").optional().or(z.literal('')),
  videoCategory: z.string({
    message: 'Video Category must string',
  }),
  viaEmail: z.boolean({
    message: 'Must be boolean',
  }),
});

export default function InspirationDialogue() {
  const { data: session } = useSession();
  const token = session?.accessToken;
  const isSubscribed = session?.user?.dailyInspiration?.isActive;
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState(false);
  const [categories, setCategories] = useState([]);
  const [fetchingCategories, setFetchingCategories] = useState(true);
  const [active, setActive] = useState('');
  const [profile, setProfile] = useState(null);
  const [clickedButton, setClickedButton] = useState('');

  // Fetch Categories API
  const fetchCategories = useCallback(async () => {
    setFetchingCategories(true);
    setCategories([]);
    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/video-categories/list/categories`,
        {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = await response.json();
      setCategories(data.data || []);
    } catch (error) {
      setCategories([]);
    } finally {
      setFetchingCategories(false);
    }
  }, [token]);

  const form = useForm({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      isActive: true,
      videoCategory: '',
      viaEmail: null,
    },
  });

  const onSubmit = async (formData) => {
    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/users/daily-inspiration-preference`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(formData),
        }
      );
      const data = await response.json();

      if (response.ok) {
        setProfile(data?.data);
        form.reset();
        setOpen(false);
        showToast(data?.message || 'Success', 'success');
      } else {
        if (response.status === 400 || response.status === 409) {
          handleApiErrors(form, data.errors);
          return;
        }
        showToast(data?.message || 'An error occurred during signin', 'error');
        return;
      }
      return;
    } catch (error) {
      showToast(error?.message || 'An error occurred during signin', 'error');
    }
  };

  const handleUnsubscribe = async () => {
    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/users/daily-inspiration-preference`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ isActive: false }),
        }
      );
      const data = await response.json();

      if (response.ok) {
        setProfile(data?.data);
        form.reset();
        setOpen(false);
        showToast(data?.message || 'Success', 'success');
      } else {
        if (response.status === 400 || response.status === 409) {
          handleApiErrors(form, data.errors);
          return;
        }
        showToast(data?.message || 'An error occurred during signin', 'error');
        return;
      }
      return;
    } catch (error) {
      showToast(error?.message || 'An error occurred during signin', 'error');
    }
  };

  // Fetch categories list
  useEffect(() => {
    if (!token) return;

    const fetchData = async () => {
      const data = await getProfile();
      if (data?.dailyInspiration?.isActive) {
        setActive(data?.dailyInspiration?.videoCategory);
        setProfile(data);
        form.setValue('viaEmail', data?.dailyInspiration?.viaEmail);
      }
      await fetchCategories();
    };
    fetchData();
  }, [token, fetchCategories]);

  {
    /* <Button variant="default" className="mb-2 rounded-full">
            <Sparkles />
            Daily Inspiration
          </Button> */
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger onClick={() => setOpen(true)} asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'border-dark-blue text-dark-blue hover:bg-periwinkle hover:text-dark-blue',
            'rounded-full px-4 py-3 text-left text-sm break-words whitespace-normal',
            'h-auto w-auto max-w-full'
          )}
        >
          Daily Inspirations
        </Button>
      </PopoverTrigger>
      <PopoverContent className="me-4 w-[380px] border-0 bg-transparent shadow-none">
        <Card className={`gap-0 rounded-lg border border-gray-200 bg-white py-4 shadow-2xl`}>
          <CardHeader className={`flex items-center justify-between pb-3`}>
            <CardTitle className={`text-primary flex items-center justify-center`}>
              <Sparkles className="mr-2" />
              <span>Start Your Daily Inspiration</span>
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setOpen(false)}
              className="h-8 w-8 p-0 hover:bg-gray-100"
            >
              <X className="h-4 w-4" />
            </Button>
          </CardHeader>
          <Separator className={`m-0 p-0`} />
          <CardContent className="pt-3">
            <p className="text-justify text-sm">
              With SmartGuy’s<sup>&reg;</sup> Daily Inspiration, you’ll receive one hand-picked
              video each day from the category that matters most to you.{' '}
              <b>Simply choose your category</b>, and we’ll do the rest. You can switch categories
              anytime or pause your daily videos whenever you’d like.
            </p>

            <div className="pt-5">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  {categories && categories.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {categories.map((category, index) => (
                        <Button
                          variant="ghost"
                          size="sm"
                          type="button"
                          className={`rounded-full bg-gray-200 ${active === category._id && 'bg-primary/80 text-white'}`}
                          key={index}
                          onClick={() => {
                            (setActive(category._id), form.setValue('videoCategory', category._id));
                          }}
                        >
                          {category.title}
                        </Button>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    {active && (
                      <>
                        {!session?.user?.emailVerified ? (
                          <Link
                            href="/email-verification"
                            className="rounded-md px-3 py-2 text-red-500 underline"
                          >
                            {' '}
                            Verify Email
                          </Link>
                        ) : (
                          <>
                            <Checkbox
                              id="terms"
                              name="email"
                              className="border-2 border-gray-600"
                              checked={form.watch('viaEmail')}
                              onCheckedChange={(checked) => {
                                form.setValue('viaEmail', !!checked);
                              }}
                            />
                            <Label htmlFor="terms">Receive Email Notifications</Label>
                          </>
                        )}
                      </>
                    )}
                  </div>

                  <div className="flex justify-end gap-1.5">
                    {profile?.dailyInspiration?.isActive && (
                      <Button
                        type="button"
                        variant="destructive"
                        className="cursor-pointer rounded-full"
                        onClick={() => {
                          setClickedButton('unsubscribe');
                          handleUnsubscribe();
                        }}
                      >
                        Unsubscribe
                        {clickedButton === 'unsubscribe' && (
                          <Loader2 className="ml-2 size-6 animate-spin" />
                        )}
                      </Button>
                    )}

                    {active && (
                      <Button
                        type="submit"
                        variant="default"
                        className="cursor-pointer rounded-full"
                        onClick={() => {
                          form.setValue('isActive', true);
                          setClickedButton('subscribe');
                        }}
                        disabled={
                          (profile?.dailyInspiration?.isActive &&
                            profile?.dailyInspiration?.videoCategory === active) ||
                          !form.getValues().viaEmail
                        }
                      >
                        {profile?.dailyInspiration?.isActive && 'Change'}
                        {!profile?.dailyInspiration?.isActive && 'Subscribe'}

                        {form.formState.isSubmitting && clickedButton === 'subscribe' && (
                          <Loader2 className="ml-2 size-6 animate-spin" />
                        )}
                      </Button>
                    )}
                  </div>
                </form>
              </Form>
            </div>
          </CardContent>
        </Card>
      </PopoverContent>
    </Popover>
  );
}
